import { Input } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import React, { useMemo, useState } from 'react';

type SearchableColumn<T> = ColumnType<T> & {
  /**
   * Optional custom key for identifying this column's filter.
   * Falls back to column.key or dataIndex.
   */
  searchKey?: string;
  /**
   * Enable/disable header search input for this column.
   * Defaults to true.
   */
  searchable?: boolean;
  /**
   * Custom way to get a string value from a record for filtering.
   * If not provided, the hook will try to read from dataIndex.
   */
  getSearchValue?: (record: T) => string | number | null | undefined;
};

export interface TableColumnSearchResult<T> {
  columnsWithSearch: ColumnsType<T>;
  filteredData: T[];
}

const headerContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  paddingBottom: 4,
};

const headerLabelRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
};

const headerLabelStyle: React.CSSProperties = {
  fontWeight: 600,
};

function getColumnKey<T>(col: SearchableColumn<T>, index: number): string {
  if (col.searchKey) return String(col.searchKey);
  if (col.key != null) return String(col.key);
  if (typeof col.dataIndex === 'string' || typeof col.dataIndex === 'number') {
    return String(col.dataIndex);
  }
  if (Array.isArray(col.dataIndex) && col.dataIndex.length) {
    return String(col.dataIndex.join('.'));
  }
  return String(index);
}

function getDeepValue(record: any, dataIndex?: ColumnType<any>['dataIndex']): unknown {
  if (!dataIndex) return undefined;

  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce((acc, key) => (acc == null ? undefined : acc[key]), record);
  }

  return record?.[dataIndex];
}

export function useTableColumnSearch<T>(
  data: T[],
  columns: SearchableColumn<T>[]
): TableColumnSearchResult<T> {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const columnsWithSearch: ColumnsType<T> = useMemo(
    () =>
      columns.map((col, index) => {
        const key = getColumnKey(col, index);
        const searchable = col.searchable !== false; // default true

        if (!searchable) {
          return col;
        }

        const existingTitle = col.title as React.ReactNode;

        const filterInput = (
          <Input
            size="small"
            placeholder="Search"
            allowClear
            value={columnFilters[key] || ''}
            onChange={(e) => {
              const value = e.target.value;
              setColumnFilters((prev) => ({
                ...prev,
                [key]: value,
              }));
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onFocus={(e) => {
              e.stopPropagation();
            }}
          />
        );

        return {
          ...col,
          width: col.width, // Explicitly preserve width
          onHeaderCell: col.onHeaderCell, // Explicitly preserve onHeaderCell for resizing
          title: (
            <div style={headerContainerStyle}>
              <div style={headerLabelRowStyle}>
                <span style={headerLabelStyle}>{existingTitle}</span>
              </div>
              {filterInput}
            </div>
          ),
        };
      }),
    [columns, columnFilters]
  );

  const filteredData: T[] = useMemo(() => {
    // If no filters, return original data
    if (!Object.values(columnFilters).some((v) => v)) {
      return data;
    }

    return data.filter((record) =>
      columns.every((col, index) => {
        const key = getColumnKey(col, index);
        const filterValue = columnFilters[key];

        if (!filterValue) return true;

        let cellValue: unknown;

        if (typeof col.getSearchValue === 'function') {
          cellValue = col.getSearchValue(record);
        } else {
          cellValue = getDeepValue(record, col.dataIndex);
        }

        return (cellValue ?? '')
          .toString()
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      })
    );
  }, [data, columns, columnFilters]);

  return {
    columnsWithSearch,
    filteredData,
  };
}


